import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, Quote } from 'lucide-react';

import magicMoonImg from '../assets/serveses/Screenshot 2026-08-20 141258.png';
import sruthiDigitalsImg from '../assets/serveses/Screenshot 2026-08-20 141323.png';
import kanakaGuruImg from '../assets/serveses/Screenshot 2026-08-20 141452.png';
import samHealthImg from '../assets/serveses/Screenshot 2026-08-20 141717.png';

// Client logos as styled text placeholders (premium look)
const clients = [
  { name: 'Magic Moon Studios', color: '#0B4B5A', img: magicMoonImg },
  { name: 'Sruthi Digitals', color: '#EAB308', img: sruthiDigitalsImg },
  { name: 'Kanaka Guru Peeta', color: '#F97316', img: kanakaGuruImg },
  { name: 'Sam Health', color: '#1E3A8A', img: samHealthImg },
  { name: 'KGF Khanana', color: '#D97706' },
  { name: 'Rume Skin Care', color: '#E11D48' },
  { name: 'Scholars World', color: '#4F46E5' },
  { name: 'TechNova', color: '#1A3BCC' },
  { name: 'DataSphere', color: '#0EA5E9' },
  { name: 'NexaCore', color: '#7C3AED' },
  { name: 'VisionAI', color: '#059669' },
  { name: 'CloudBase', color: '#DC2626' },
  { name: 'PixelForge', color: '#D97706' },
  { name: 'SmartSys', color: '#1A3BCC' },
  { name: 'EduPlus', color: '#0EA5E9' },
  { name: 'FinEdge', color: '#7C3AED' },
  { name: 'MediLink', color: '#059669' },
  { name: 'AgriTech', color: '#D97706' },
  { name: 'RetailIQ', color: '#DC2626' },
];

const testimonials = [
  {
    name:    'Rajesh Sharma',
    role:    'CTO, TechNova Solutions',
    text:    "QuBright delivered our AI platform in record time with exceptional quality. Their team's technical depth and communication are truly outstanding.",
    rating:  5,
    initial: 'R',
    color:   'bg-brand-blue',
  },
  {
    name:    'Priya Menon',
    role:    'Head of Engineering, DataSphere',
    text:    "From mobile apps to backend infrastructure, QuBright handled everything seamlessly. They're not just a vendor — they're a technology partner.",
    rating:  5,
    initial: 'P',
    color:   'bg-brand-orange',
  },
  {
    name:    'Arun Krishnan',
    role:    'Founder, NexaCore',
    text:    "The SDP program QuBright conducted at our college was a game-changer for our students. Real projects, real mentorship, real results.",
    rating:  5,
    initial: 'A',
    color:   'bg-violet-600',
  },
];

export default function Clients() {
  const [titleRef, titleInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const doubledClients = [...clients, ...clients];

  return (
    <section id="clients" className="py-28 bg-brand-light relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          ref={titleRef}
          initial={{ y: 30, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="section-label mb-5 inline-block">Trusted By</span>
          <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-5">
            Clients Who Trust <span className="gradient-text-blue">QuBright</span>
          </h2>
          <p className="font-inter text-slate-500 text-lg max-w-xl mx-auto">
            From startups to enterprises, we've delivered technology solutions that scale
            and create lasting impact.
          </p>
        </motion.div>

        {/* Marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="overflow-hidden mb-20 relative"
        >
          {/* Fade edges */}
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-brand-light to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-brand-light to-transparent z-10 pointer-events-none" />

          <div className="marquee-track">
            {doubledClients.map((client, i) => (
              <div
                key={i}
                className="flex items-center justify-center mx-6 shrink-0"
              >
                <div className="glass-card px-8 py-4 rounded-2xl flex items-center justify-center border border-slate-100 shadow-card hover:shadow-royal transition-shadow duration-300 min-w-[160px] h-[80px]">
                  {client.img ? (
                    <img src={client.img} alt={client.name} className="max-h-full max-w-full object-contain mix-blend-multiply" />
                  ) : (
                    <>
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-syne font-bold shrink-0 mr-3"
                        style={{ background: client.color }}
                      >
                        {client.name[0]}
                      </div>
                      <span className="font-space font-semibold text-slate-700 text-sm whitespace-nowrap">
                        {client.name}
                      </span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h3 className="font-syne font-bold text-3xl text-slate-900">What Our Clients Say</h3>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.6 }}
              className="bg-white rounded-3xl p-7 border border-slate-100 shadow-card service-card"
            >
              {/* Quote icon */}
              <Quote size={28} className="text-brand-blue/20 mb-4" />

              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(t.rating)].map((_, j) => (
                  <Star key={j} size={14} className="text-amber-400 fill-amber-400" />
                ))}
              </div>

              <p className="font-inter text-slate-600 text-sm leading-relaxed mb-6 italic">
                "{t.text}"
              </p>

              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${t.color} flex items-center justify-center text-white font-syne font-bold text-sm`}>
                  {t.initial}
                </div>
                <div>
                  <p className="font-space font-semibold text-slate-900 text-sm">{t.name}</p>
                  <p className="font-inter text-slate-500 text-xs">{t.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
